import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  PlusOutlined,
  StarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

const PayrollBanksTab = ({
  banks,
  paymentAccounts,
  saving,
  saveBank,
  deleteBank,
  savePaymentAccount,
  deletePaymentAccount,
  setDefaultPaymentAccount,
}) => {
  const [bankForm] = Form.useForm();
  const [accountForm] = Form.useForm();
  const [bankOpen, setBankOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const paymentBankId = Form.useWatch("bankId", accountForm);
  const paymentAccountActive = Form.useWatch("isActive", accountForm);
  const paymentBank = banks.find((bank) => bank.bankId === paymentBankId);

  const openBank = (bank = null) => {
    setSelectedBank(bank);
    bankForm.resetFields();
    bankForm.setFieldsValue(
      bank || {
        requiresBranchCode: false,
        accountNumberMinLength: 6,
        accountNumberMaxLength: 30,
        accountNumberPattern: "^[A-Za-z0-9-]+$",
        isActive: true,
      },
    );
    setBankOpen(true);
  };

  const submitBank = async () => {
    const values = await bankForm.validateFields();
    const result = await saveBank(selectedBank?.bankId, values);
    if (result) setBankOpen(false);
  };

  const openAccount = (account = null) => {
    setSelectedAccount(account);
    accountForm.resetFields();
    accountForm.setFieldsValue(
      account
        ? {
            ...account,
            accountNumber: "",
            effectiveFrom: account.effectiveFrom
              ? dayjs(account.effectiveFrom)
              : null,
            effectiveTo: account.effectiveTo
              ? dayjs(account.effectiveTo)
              : null,
          }
        : {
            currency: "UGX",
            effectiveFrom: dayjs(),
            isDefault: false,
            isActive: true,
          },
    );
    setAccountOpen(true);
  };

  const submitAccount = async () => {
    const values = await accountForm.validateFields();
    const result = await savePaymentAccount(selectedAccount?.paymentAccountId, {
      ...values,
      effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
    });
    if (result) setAccountOpen(false);
  };

  const bankColumns = [
    {
      title: "Bank",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.name}</Text>
          {(row.code || row.swiftCode) && (
            <Text type="secondary">
              {[row.code, row.swiftCode].filter(Boolean).join(" · ")}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Account validation",
      render: (_, row) => (
        <Text>
          {row.accountNumberMinLength}–{row.accountNumberMaxLength} characters
          {row.requiresBranchCode ? " · Branch code required" : ""}
        </Text>
      ),
    },
    {
      title: "Status",
      render: (_, row) => (
        <Tag color={row.isActive ? "success" : "default"}>
          {row.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "",
      width: 54,
      align: "center",
      render: (_, row) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined /> },
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openBank(row);
              if (key === "delete")
                Modal.confirm({
                  title: `Delete ${row.name}?`,
                  okText: "Delete",
                  okButtonProps: { danger: true },
                  onOk: () => deleteBank(row.bankId),
                });
            },
          }}
        >
          <Button type="text" size="small" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const accountColumns = [
    {
      title: "Payment account",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{row.name}</Text>
            {row.isDefault && <Tag color="gold">Default</Tag>}
          </Space>
          <Text type="secondary">
            {row.bankName} · {row.maskedAccountNumber}
          </Text>
        </Space>
      ),
    },
    { title: "Account name", dataIndex: "accountName" },
    { title: "Currency", dataIndex: "currency", width: 90 },
    {
      title: "Effective",
      render: (_, row) =>
        `${dayjs(row.effectiveFrom).format("DD MMM YYYY")} — ${row.effectiveTo ? dayjs(row.effectiveTo).format("DD MMM YYYY") : "Open"}`,
    },
    {
      title: "Status",
      render: (_, row) => (
        <Tag color={row.isActive ? "success" : "default"}>
          {row.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "",
      width: 54,
      align: "center",
      render: (_, row) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined /> },
              ...(!row.isDefault
                ? [
                    {
                      key: "default",
                      label: "Make default",
                      icon: <StarOutlined />,
                    },
                  ]
                : []),
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openAccount(row);
              if (key === "default")
                setDefaultPaymentAccount(row.paymentAccountId);
              if (key === "delete")
                Modal.confirm({
                  title: `Delete ${row.name}?`,
                  okText: "Delete",
                  okButtonProps: { danger: true },
                  onOk: () => deletePaymentAccount(row.paymentAccountId),
                });
            },
          }}
        >
          <Button type="text" size="small" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <Space>
            <BankOutlined />
            <span>Bank master</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openBank()}
          >
            Add bank
          </Button>
        }
      >
        <Text type="secondary">
          These rules validate employee and school account details before
          payroll.
        </Text>
        <Table
          style={{ marginTop: 16 }}
          rowKey="bankId"
          columns={bankColumns}
          dataSource={banks}
          pagination={false}
        />
      </Card>

      <Card
        title="School payroll payment accounts"
        extra={
          <Button
            icon={<PlusOutlined />}
            disabled={!banks.some((row) => row.isActive)}
            onClick={() => openAccount()}
          >
            Add payment account
          </Button>
        }
      >
        <Text type="secondary">
          The selected account is the school account from which salaries will be
          paid. Account numbers remain masked on screen.
        </Text>
        <Table
          style={{ marginTop: 16 }}
          rowKey="paymentAccountId"
          columns={accountColumns}
          dataSource={paymentAccounts}
          pagination={false}
        />
      </Card>

      <Modal
        title={selectedBank ? "Edit bank" : "Add bank"}
        open={bankOpen}
        onCancel={() => setBankOpen(false)}
        onOk={submitBank}
        confirmLoading={saving}
        width={720}
      >
        <Form form={bankForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Bank name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="code" label="Bank code (optional)">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="swiftCode" label="SWIFT code">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="requiresBranchCode"
                label="Require employee branch code"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountNumberMinLength"
                label="Minimum account-number length"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountNumberMaxLength"
                label="Maximum account-number length"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="accountNumberPattern"
                label="Allowed account-number pattern"
                rules={[{ required: true }]}
                extra="The default accepts letters, numbers and hyphens."
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={
          selectedAccount
            ? "Edit school payment account"
            : "Add school payment account"
        }
        open={accountOpen}
        onCancel={() => setAccountOpen(false)}
        onOk={submitAccount}
        confirmLoading={saving}
        width={760}
      >
        <Form form={accountForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Internal name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Main salary account" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bankId"
                label="Bank"
                rules={[{ required: true }]}
              >
                <Select
                  options={banks
                    .filter(
                      (row) =>
                        row.isActive || row.bankId === selectedAccount?.bankId,
                    )
                    .map((row) => ({
                      value: row.bankId,
                      label: row.code ? `${row.name} (${row.code})` : row.name,
                    }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="branchName" label="Branch name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="branchCode"
                label="Branch code"
                rules={
                  paymentBank?.requiresBranchCode
                    ? [
                        {
                          required: true,
                          message: "This bank requires a branch code.",
                        },
                      ]
                    : []
                }
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountName"
                label="Account name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountNumber"
                label={
                  selectedAccount
                    ? `Account number (${selectedAccount.maskedAccountNumber})`
                    : "Account number"
                }
                rules={selectedAccount ? [] : [{ required: true }]}
              >
                <Input
                  placeholder={
                    selectedAccount
                      ? "Leave blank to keep the current number"
                      : ""
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="effectiveFrom"
                label="Effective from"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effectiveTo" label="Effective to">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isDefault"
                label="Default payroll account"
                valuePropName="checked"
              >
                <Switch disabled={!paymentAccountActive} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch
                  onChange={(checked) => {
                    if (!checked) accountForm.setFieldValue("isDefault", false);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollBanksTab;
